import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AtSymbolIcon,
  BuildingOfficeIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  HandThumbUpIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import notificationApi from '../../services/notificationApi';
import intranetApi from '../../services/intranetApi';
import { formatDistanceToNow } from 'date-fns';
import { renderTextWithMentions, renderMentionsAsHTML } from '../../utils/mentions';
import PostLikes from './PostLikes';
import PostBookmark from './PostBookmark';
import MentionModal from '../common/MentionModal';

const Mentions = () => {
  const [activeTab, setActiveTab] = useState('user'); // 'user' or 'department'
  const [userMentions, setUserMentions] = useState([]);
  const [departmentMentions, setDepartmentMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mentionCounts, setMentionCounts] = useState({
    user_mentions: 0,
    department_mentions: 0,
    total_mentions: 0
  });
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [expandedComments, setExpandedComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [showMentionModal, setShowMentionModal] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(null);

  useEffect(() => {
    loadMentions();
    loadMentionCounts();
  }, []);

  const loadMentions = async () => {
    try {
      setLoading(true);
      const [userResponse, deptResponse] = await Promise.all([
        notificationApi.getUserMentions({ limit: 50 }),
        notificationApi.getDepartmentMentions({ limit: 50 })
      ]);

      setUserMentions(userResponse.mentions || []);
      setDepartmentMentions(deptResponse.mentions || []);
    } catch (error) {
      console.error('Error loading mentions:', error);
      toast.error('Failed to load mentions');
    } finally {
      setLoading(false);
    }
  };

  const loadMentionCounts = async () => {
    try {
      const response = await notificationApi.getMentionCounts();
      setMentionCounts(response.counts);
    } catch (error) {
      console.error('Error loading mention counts:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      
      // Update the mention in the appropriate list
      const updateMention = (mention) => 
        mention.id === notificationId ? { ...mention, is_read: true } : mention;
      
      setUserMentions(prev => prev.map(updateMention));
      setDepartmentMentions(prev => prev.map(updateMention));
      
      // Update counts
      loadMentionCounts();
      
      toast.success('Mention marked as read');
    } catch (error) {
      console.error('Error marking mention as read:', error);
      toast.error('Failed to mark mention as read');
    }
  };

  const handleToggleLike = async (postId, currentLikeStatus) => {
    try {
      const response = await intranetApi.posts.toggleLike(postId);
      
      if (response.data.success) {
        // Update the post in both mention lists
        const updatePost = (mention) => {
          if (mention.related_post_id === postId) {
            return {
              ...mention,
              // Update like status in the mention data if it exists
              user_liked: response.data.user_liked,
              like_count: response.data.like_count
            };
          }
          return mention;
        };
        
        setUserMentions(prev => prev.map(updatePost));
        setDepartmentMentions(prev => prev.map(updatePost));
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like status');
    }
  };

  const handleToggleBookmark = async (postId, currentBookmarkStatus) => {
    try {
      const response = await intranetApi.posts.toggleBookmark(postId);
      
      if (response.data.success) {
        // Update the post in both mention lists
        const updatePost = (mention) => {
          if (mention.related_post_id === postId) {
            return {
              ...mention,
              user_bookmarked: response.data.user_bookmarked,
              bookmark_count: response.data.bookmark_count
            };
          }
          return mention;
        };
        
        setUserMentions(prev => prev.map(updatePost));
        setDepartmentMentions(prev => prev.map(updatePost));
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      toast.error('Failed to update bookmark status');
    }
  };

  const togglePostExpansion = (postId) => {
    setExpandedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: null }));
    } else {
      try {
        const response = await intranetApi.posts.getComments(postId);
        setExpandedComments(prev => ({ 
          ...prev, 
          [postId]: response.data.comments || [] 
        }));
      } catch (error) {
        console.error('Error loading comments:', error);
        toast.error('Failed to load comments');
      }
    }
  };

  const handleAddComment = async (postId) => {
    const content = newComments[postId];
    if (!content?.trim()) return;

    try {
      const response = await intranetApi.posts.addComment(postId, { content });
      
      if (response.data.success) {
        // Add the new comment to the expanded comments
        setExpandedComments(prev => ({
          ...prev,
          [postId]: [...(prev[postId] || []), response.data.comment]
        }));
        
        // Clear the comment input
        setNewComments(prev => ({ ...prev, [postId]: '' }));
        
        toast.success('Comment added successfully');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleCommentChange = (postId, value) => {
    setNewComments(prev => ({ ...prev, [postId]: value }));
  };

  const openMentionModal = (postId) => {
    setCurrentPostId(postId);
    setShowMentionModal(true);
  };

  const handleMentionSelect = (mention) => {
    const postId = currentPostId;
    const currentContent = newComments[postId] || '';
    const newContent = currentContent + mention + ' ';
    setNewComments(prev => ({ ...prev, [postId]: newContent }));
    setShowMentionModal(false);
  };

  const renderMention = (mention) => {
    const isPost = mention.post_id;
    const content = isPost ? mention.post_content : mention.comment_content;
    const title = mention.post_title;
    const createdAt = isPost ? mention.post_created_at : mention.comment_created_at;
    const postId = mention.related_post_id;
    
    const isExpanded = expandedPosts.has(postId);
    const shouldTruncate = content && content.length > 300;
    const displayContent = shouldTruncate && !isExpanded 
      ? content.substring(0, 300) + '...' 
      : content;

    return (
      <div 
        key={mention.id} 
        className={`bg-white rounded-lg shadow border-l-4 p-6 ${
          mention.is_read ? 'border-l-gray-300' : 'border-l-indigo-500'
        }`}
      >
        {/* Mention Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${
              mention.mentioned_department_id ? 'bg-blue-100' : 'bg-indigo-100'
            }`}>
              {mention.mentioned_department_id ? (
                <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
              ) : (
                <AtSymbolIcon className="h-5 w-5 text-indigo-600" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {mention.mentioned_by_name} mentioned {
                  mention.mentioned_department_id 
                    ? `your department (${mention.department_name})`
                    : 'you'
                } in {isPost ? 'a post' : 'a comment'}
              </p>
              <p className="text-xs text-gray-500">
                {formatDistanceToNow(new Date(mention.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {!mention.is_read && (
              <button
                onClick={() => handleMarkAsRead(mention.id)}
                className="p-1 text-indigo-600 hover:text-indigo-800 transition-colors"
                title="Mark as read"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Post/Comment Content */}
        <div className="mb-4">
          <Link 
            to={`/company/feed`}
            className="text-lg font-semibold text-gray-900 hover:text-indigo-600 transition-colors"
          >
            {title}
          </Link>
          
          <div className="mt-2 text-gray-700">
            <div dangerouslySetInnerHTML={{ 
              __html: renderMentionsAsHTML(displayContent || '') 
            }} />
            
            {shouldTruncate && (
              <button
                onClick={() => togglePostExpansion(postId)}
                className="mt-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
              >
                {isExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        </div>

        {/* Post Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-4">
            <PostLikes
              postId={postId}
              likeCount={mention.like_count || 0}
              userLiked={mention.user_liked || false}
              onToggleLike={() => handleToggleLike(postId, mention.user_liked)}
            />
            <PostBookmark
              postId={postId}
              bookmarkCount={mention.bookmark_count || 0}
              userBookmarked={mention.user_bookmarked || false}
              onToggleBookmark={handleToggleBookmark}
            />
            <button
              onClick={() => toggleComments(postId)}
              className="flex items-center space-x-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
              <span className="text-sm">
                {expandedComments[postId] ? 'Hide' : 'Show'} Comments
              </span>
            </button>
          </div>
          
          <Link
            to={`/company/feed`}
            className="flex items-center space-x-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <EyeIcon className="h-5 w-5" />
            <span className="text-sm">View Post</span>
          </Link>
        </div>

        {/* Comments Section */}
        {expandedComments[postId] && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {/* Existing Comments */}
            {expandedComments[postId].length > 0 && (
              <div className="space-y-3 mb-4">
                {expandedComments[postId].map(comment => (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {comment.author_name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div 
                      className="text-sm text-gray-700"
                      dangerouslySetInnerHTML={{ 
                        __html: renderMentionsAsHTML(comment.content) 
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment */}
            <div className="flex space-x-3">
              <div className="flex-1">
                <textarea
                  value={newComments[postId] || ''}
                  onChange={(e) => handleCommentChange(postId, e.target.value)}
                  placeholder="Add a comment... (use @ to mention someone)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  rows="2"
                />
              </div>
              <div className="flex flex-col space-y-1">
                <button
                  onClick={() => openMentionModal(postId)}
                  className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                  title="Add mention"
                >
                  <AtSymbolIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleAddComment(postId)}
                  disabled={!newComments[postId]?.trim()}
                  className="p-2 text-indigo-600 hover:text-indigo-800 disabled:text-gray-300 transition-colors"
                  title="Send comment"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading mentions...</p>
        </div>
      </div>
    );
  }

  const currentMentions = activeTab === 'user' ? userMentions : departmentMentions;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Mentions</h1>
          <p className="mt-2 text-gray-600">
            Stay updated on posts and comments where you or your department were mentioned
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('user')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'user'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <AtSymbolIcon className="h-5 w-5" />
                  <span>My Mentions</span>
                  {mentionCounts.user_mentions > 0 && (
                    <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded-full">
                      {mentionCounts.user_mentions}
                    </span>
                  )}
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('department')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'department'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-5 w-5" />
                  <span>My Dept Mentions</span>
                  {mentionCounts.department_mentions > 0 && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                      {mentionCounts.department_mentions}
                    </span>
                  )}
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Mentions List */}
        <div className="space-y-6">
          {currentMentions.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                {activeTab === 'user' ? (
                  <AtSymbolIcon className="h-12 w-12" />
                ) : (
                  <BuildingOfficeIcon className="h-12 w-12" />
                )}
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No {activeTab === 'user' ? 'personal' : 'department'} mentions yet
              </h3>
              <p className="mt-2 text-gray-500">
                {activeTab === 'user' 
                  ? "When someone mentions you with @yourname, it will appear here."
                  : "When someone mentions your department, it will appear here."
                }
              </p>
            </div>
          ) : (
            currentMentions.map(renderMention)
          )}
        </div>

        {/* Mention Modal */}
        {showMentionModal && (
          <MentionModal
            onClose={() => setShowMentionModal(false)}
            onMentionSelect={handleMentionSelect}
          />
        )}
      </div>
    </div>
  );
};

export default Mentions; 